'use client'

import { useEffect } from 'react'

import { gaId } from '@/lib/config'
import { trackErrorPageView } from '@/lib/track-error'

/**
 * Last-resort error boundary for failures inside the root layout itself.
 * Stays visually consistent with the HLLBNT preview gate using only inline
 * styles + inline SVG, since the rest of the app's CSS may not have loaded.
 * The branded site UI lives in src/app/(site)/error.tsx and the admin shell
 * uses src/app/(payload)/error.tsx. This is the absolute fallback.
 */
export default function GlobalError({ error }: { error: Error & { digest?: string } }) {
  useEffect(() => {
    console.error('Root layout error:', error)

    // Root layout failed, so the layout-level gtag <Script> never ran.
    // Bootstrap gtag inline so this fatal error still reaches GA4.
    const w = window as Window & { dataLayer?: unknown[] }
    if (gaId && typeof window.gtag !== 'function') {
      w.dataLayer = w.dataLayer ?? []
      window.gtag = function gtagShim(...args: unknown[]) {
        w.dataLayer?.push(args)
      } as Window['gtag']
      ;(window.gtag as unknown as (cmd: string, action: unknown) => void)('js', new Date())
      window.gtag('config', gaId, { send_page_view: false })
      const tag = document.createElement('script')
      tag.async = true
      tag.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`
      document.head.appendChild(tag)
    }

    trackErrorPageView({
      kind: 'global_error',
      description: error.message || 'Unknown global error',
      digest: error.digest,
      fatal: true,
    })
  }, [error])

  return (
    <html lang="en-AU">
      <body
        style={{
          backgroundColor: '#0a0a0a',
          backgroundImage: 'radial-gradient(rgba(255, 0, 255, 0.07) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          color: '#ffffff',
          fontFamily: '"SF Mono", ui-monospace, Menlo, Consolas, "Liberation Mono", monospace',
          margin: 0,
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: '32rem', width: '100%' }}>
          {/* HLLBNT mark inside rim glow ring */}
          <div
            style={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '10rem',
              height: '10rem',
              borderRadius: '9999px',
              border: '1px solid rgba(255, 0, 255, 0.2)',
              marginBottom: '2.25rem',
              background:
                'conic-gradient(from 0deg, transparent 0deg, rgba(255, 0, 175, 0.1) 45deg, transparent 90deg, transparent 160deg, rgba(255, 0, 255, 0.15) 210deg, transparent 260deg, rgba(255, 0, 215, 0.07) 320deg, transparent 360deg)',
            }}
          >
            <svg
              viewBox="0 0 283.46 283.46"
              width="80"
              height="80"
              aria-hidden="true"
              style={{ color: '#ffffff' }}
            >
              <polygon
                fill="currentColor"
                points="26.05,33.64 26.05,126.73 20.21,128.89 26.05,130.35 26.05,236.11 41.74,236.11 41.74,141.47 56.73,141.47 56.73,236.11 73.03,236.11 73.03,130.78 77.68,128.75 73.03,126.77 73.03,33.64 57.6,33.64 57.6,117.76 41.91,117.76 32.95,33.64 "
              />
              <path
                fill="currentColor"
                d="M79.52,33.64v202.47h30.6c0,0-2.07-3.36-2.07-11.64s4.14-13.1,4.14-13.1H98.05L87.36,33.64H79.52z"
              />
              <path
                fill="currentColor"
                d="M112.18,33.64l1.84,177.71c0,0-2.61,5.28-1.61,13.81c0.89,7.57,7.41,10.95,7.41,10.95h17.24h1.14c0,0,6.52-3.37,7.41-10.95c1-8.53-1.61-13.81-1.61-13.81l-13.33,0.01l-10-177.72H112.18z"
              />
              <path
                fill="currentColor"
                d="M180.02,171.72c-0.26-12.15-2.33-38.53-6.21-45.51c0,0,3.1-9.31,3.36-23.01s-0.26-69.56-0.26-69.56l-2.59-6.34l-2.84,6.34h-18.36l-3.23-6.34l-3.15,6.34v177.65c0,0,4.31,6.46,4.83,13.79c0.39,5.46-1.72,11.03-1.72,11.03h27.24C177.09,236.11,180.27,183.87,180.02,171.72z M157.69,203.27v-60.85l9.48,31.72L157.69,203.27z M157.69,116.9V56.05l9.48,31.72L157.69,116.9z"
              />
              <path
                fill="currentColor"
                d="M208.37,33.64v122.05L191.82,33.64h-7.24v202.47h16.02c0,0-2.18-3.62-1.92-12.62c0.28-9.7,5.03-12.28,5.03-12.28s-1.75,4.09-1.75,12.45c0,8.36,7.56,12.45,7.56,12.45h10.48V33.64H208.37z"
              />
              <path
                fill="currentColor"
                d="M249.74,234.33V56.05h6.98V33.64l-2.33-6.34l-2.84,6.34h-23.35l-3.07-6.34l-3.07,6.34v22.41h6.13v180.06c0,0,7.56-4.09,7.56-12.45c0-8.36-1.75-12.45-1.75-12.45s4.75,2.58,5.03,12.28c0.26,9-1.92,12.62-1.92,12.62l17.81,3.68l-3.62,3.45l-232.9,1.72l-0.17-4.14l-7.76,5.86l7.59,6.38v-4.14l251,7.24l7.59-18.62L249.74,234.33z"
              />
            </svg>
          </div>

          <p
            style={{
              color: '#ff00af',
              fontSize: '0.7rem',
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              marginBottom: '1.25rem',
            }}
          >
            Rock&apos;n Earthworks
          </p>

          <h1
            style={{
              fontSize: '2.25rem',
              fontWeight: 700,
              margin: 0,
              letterSpacing: '0.02em',
            }}
          >
            500: Crashed Out
          </h1>

          <p
            style={{
              color: 'rgba(255, 255, 255, 0.6)',
              marginTop: '1rem',
              fontSize: '0.95rem',
              lineHeight: 1.55,
            }}
          >
            Something fell over hard. Reload the page or head home.
          </p>

          <div
            style={{
              marginTop: '2rem',
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages -- root layout is broken so Link/router is unsafe; force a hard navigation */}
            <a
              href="/"
              style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                backgroundColor: '#ff00ff',
                color: '#ffffff',
                textDecoration: 'none',
                borderRadius: '0.25rem',
                fontWeight: 600,
                fontSize: '0.85rem',
                letterSpacing: '0.04em',
              }}
            >
              Back home
            </a>
          </div>

          {error.digest && (
            <p
              style={{
                marginTop: '2.5rem',
                fontSize: '0.7rem',
                color: 'rgba(255, 255, 255, 0.3)',
                letterSpacing: '0.15em',
              }}
            >
              Reference: {error.digest}
            </p>
          )}

          <p
            style={{
              marginTop: '0.75rem',
              fontSize: '0.65rem',
              color: 'rgba(255, 0, 215, 0.4)',
              letterSpacing: '0.25em',
            }}
          >
            67 65 74 62 6E 74
          </p>
        </div>
      </body>
    </html>
  )
}
